/**
 * Utility functions for standardized user name display
 * ALWAYS use firstName + lastName from the users collection
 */

import { getDoc } from 'firebase/firestore';
import { profileDocRef } from "@/lib/public-profile";

/**
 * Fetches and returns the user's full name from the users collection
 * @param userId - The user's ID
 * @returns Promise<string> - Full name as "FirstName LastName" or fallback
 */
export async function getUserDisplayName(userId: string): Promise<string> {
  if (!userId) return 'User';
  
  try {
    const userDoc = await getDoc(profileDocRef(userId));
    if (!userDoc.exists()) return 'User';
    
    const userData = userDoc.data();
    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return 'User';
  } catch (error) {
    console.error('Error fetching user display name:', error);
    return 'User';
  }
}

/**
 * Formats a name with last initial only (e.g., "John D.")
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @returns string - Formatted name with last initial
 */
export function formatNameWithInitial(firstName: string, lastName: string): string {
  if (!firstName || !lastName) return firstName || 'User';
  return `${firstName} ${lastName[0]}.`;
}

/**
 * Truncates a full name string so the last name is just the first letter.
 * e.g., "Immanuelle Iferee" → "Immanuelle I."
 * If the name has no space (single name), returns as-is.
 */
export function truncateLastName(fullName: string): string {
  if (!fullName) return 'User';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  const firstName = parts.slice(0, -1).join(' ');
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}.`;
}

/**
 * Truncates only when the input *looks like* a person's name — exactly two
 * Latin-letter words, both starting with a capital, no apostrophes/digits/
 * punctuation. Anything else (store names like "The Best Store Ever!",
 * "Callie's Test Store", brands with numbers, single words) is returned
 * untouched. Use this on public surfaces where the same field can hold
 * either a person's name OR a custom brand.
 */
export function truncateLastNameIfPerson(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 2) return trimmed;
  const personLike = /^[A-Z][a-zA-Z-]+$/;
  if (!personLike.test(parts[0]) || !personLike.test(parts[1])) return trimmed;
  return `${parts[0]} ${parts[1][0]}.`;
}

/**
 * Gets initials from first and last name (e.g., "JD")
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @returns string - Two-letter initials
 */
export function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first && last ? `${first}${last}` : first || last || 'U';
}

/** Fields used for professional listing display (Firestore professionalProfiles). */
export type ProfessionalNameFields = {
  firstName?: string;
  lastName?: string;
  /** Legacy-only field; ignored for display derivation. */
  displayName?: string;
};

/** Full name from users-derived first + last only. */
export function professionalFullDisplayName(p: ProfessionalNameFields | null | undefined): string {
  if (!p) return '';
  const joined = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return joined;
}

/** Short label (e.g. "Jamie H.") from users-derived first + last only. */
export function professionalShortDisplayName(p: ProfessionalNameFields | null | undefined): string {
  if (!p) return '';
  if (p.firstName?.trim() && p.lastName?.trim()) {
    return formatNameWithInitial(p.firstName.trim(), p.lastName.trim());
  }
  return p.firstName?.trim() || '';
}

export function professionalShortDisplayNameOr(
  p: ProfessionalNameFields | null | undefined,
  fallback: string,
): string {
  const s = professionalShortDisplayName(p);
  return s || fallback;
}

/**
 * The canonical *public* display name for the signed-in user, used wherever
 * a name gets stamped onto outbound traffic (job postings, message sender
 * names, review author names, notifications, etc.). Always derives from
 * `users/{uid}.firstName + lastName` first — the same source the
 * professional profile uses — so a user's name is identical across surfaces.
 *
 * Returns "Sarah J." for paid users and falls through to firstName, then
 * Firebase auth `displayName`, then a generic fallback. Never returns the
 * raw auth `displayName` if the profile name is present, because Google-
 * imported nicknames ("Ellie") were what was making messages, jobs, and
 * profiles look like different people.
 *
 * Use `getCurrentUserShortDisplayName` for public/listing surfaces and
 * `getCurrentUserFullDisplayName` for the user's own private surfaces (e.g.
 * "Welcome, Sarah Johnson").
 */
type AuthLikeUser = {
  displayName?: string | null;
  email?: string | null;
  profile?: { firstName?: string; lastName?: string } | null;
};

export function getCurrentUserShortDisplayName(
  user: AuthLikeUser | null | undefined,
  fallback = 'User',
): string {
  if (!user) return fallback;
  const firstName = user.profile?.firstName?.trim();
  const lastName = user.profile?.lastName?.trim();
  if (firstName && lastName) return formatNameWithInitial(firstName, lastName);
  if (firstName) return firstName;
  return user.displayName?.trim() || user.email?.split('@')[0] || fallback;
}

export function getCurrentUserFullDisplayName(
  user: AuthLikeUser | null | undefined,
  fallback = 'User',
): string {
  if (!user) return fallback;
  const firstName = user.profile?.firstName?.trim();
  const lastName = user.profile?.lastName?.trim();
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  return user.displayName?.trim() || user.email?.split('@')[0] || fallback;
}

